import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useSectionAnimationContext } from '../contexts/SectionAnimationContext';
import { ExternalLink, Github, Eye, Code, Zap, TrendingUp, X, Calendar, Users, Target, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { projects } from '../data/projects';
import LiquidBlobs from './LiquidBlobs';

const TechProjectsSection = () => {
  const [filter, setFilter] = useState('all');
  const [hoveredProject, setHoveredProject] = useState(null);
  const scrollContainer = React.useRef(null);
  const scrollContainer2 = React.useRef(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const { getSectionClasses } = useSectionAnimationContext();

  // Use static data instead of API call
  const projectsData = projects;

  const scrollLeft = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const scrollLeft2 = () => {
    if (scrollContainer2.current) {
      scrollContainer2.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight2 = () => {
    if (scrollContainer2.current) {
      scrollContainer2.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-500 text-white';
      case 'In Progress': return 'bg-blue-500 text-white';
      case 'In Development': return 'bg-purple-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
  };

  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setSelectedProject(null);
  };

  const categories = ['all', 'Data Analytics & AI', 'Process Automation', 'AI & Productivity', 'Data Engineering'];
  
  const filteredProjects = projectsData
    .filter(project => filter === 'all' || project.category === filter)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)); // Sort by newest first

  return (
    <section id="projects" className="py-24 bg-gradient-to-b from-gray-50 to-gray-100 relative overflow-hidden">
      <LiquidBlobs section="projects" />
      <div className="container mx-auto px-6 relative z-10">
        {/* Section content with genie animation */}
        <div className={getSectionClasses('projects')}>
          {/* Section Header */}
          <div className="mb-16 text-center">
            <h2 className="heading-1 text-5xl md:text-7xl font-black text-text-primary mb-4 font-grotesk uppercase">
              Tech <span className="text-purple-600">Projects</span>
            </h2>
            <div className="w-24 h-1 bg-purple-600 mx-auto mb-6"></div>
            <p className="body-medium text-text-secondary max-w-3xl mx-auto">
              Innovation-driven projects showcasing automation, AI implementation, and process optimization 
              that delivered measurable business impact.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                className={`btn-base ${filter === category ? 'btn-purple' : 'btn-secondary'}`}
              >
                {category === 'all' ? 'All Projects' : category}
              </Button>
            ))}
          </div>

          {/* Featured Project */}
          {filteredProjects.length > 0 && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <Badge variant="outline" className="bg-purple-600/10 text-purple-600 border-purple-600/30">
                  Featured Project
                </Badge>
              </div>
              
              <Card
                className="group overflow-hidden rounded-2xl glass-card hover:scale-[1.01] transition-all duration-500 cursor-pointer"
                onMouseEnter={() => setHoveredProject(filteredProjects[0].id)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => handleViewProject(filteredProjects[0])}
              >
                <div className="md:flex">
                  {/* Featured Image */}
                  <div className="md:w-1/2 relative overflow-hidden rounded-t-2xl md:rounded-t-none md:rounded-l-2xl">
                    <img
                      src={filteredProjects[0].image || '/api/placeholder/500/320'}
                      alt={filteredProjects[0].title}
                      className="block w-full h-64 md:h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => { e.target.src = '/api/placeholder/500/320'; }}
                    />
                    {/* keep a gentle bottom fade only (avoid covering the top edge) */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/90 via-white/0 to-transparent"></div>

                    {/* Status Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className={`font-semibold ${filteredProjects[0].status === 'Completed' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}`}>
                        {filteredProjects[0].status}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:w-1/2 p-8 flex flex-col justify-center">
                    <Badge className="bg-purple-600 text-white font-medium mb-4">
                      {filteredProjects[0].category}
                    </Badge>
                    
                    <h3 className="heading-3 text-text-primary mb-4 group-hover:text-gradient transition-colors duration-300">
                      {filteredProjects[0].title}
                    </h3>
                    
                    <div
                      className="body-medium text-text-secondary mb-6 rich-text-content"
                      dangerouslySetInnerHTML={{ __html: filteredProjects[0].description }}
                    />
                    
                    {/* Technologies */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-text-primary mb-3">Technologies Used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {filteredProjects[0].technologies?.slice(0, 6)?.map((tech, index) => (
                          <Badge key={index} className="bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium">
                            {tech}
                          </Badge>
                        ))}
                        {filteredProjects[0].technologies?.length > 6 && (
                          <Badge className="bg-gray-100 text-gray-600 px-3 py-1 text-sm">
                            +{filteredProjects[0].technologies.length - 6}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Impact */}
                    <div className="flex items-start space-x-2 mb-6">
                      <TrendingUp size={16} className="text-emerald-600 mt-1 flex-shrink-0" />
                      <div className="text-sm text-text-secondary">
                        <span className="font-semibold text-emerald-600">Impact:</span>
                        <div
                          className="inline rich-text-content ml-1"
                          dangerouslySetInnerHTML={{ __html: filteredProjects[0].impact }}
                        />
                      </div>
                    </div>

                    {/* Updated Date */}
                    <div className="text-sm text-text-muted mb-6">
                      Updated: {new Date(filteredProjects[0].updated_at).toLocaleDateString('en-US', { 
                        year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </div>
                    
                    <div className="flex gap-3">
                      <Button onClick={(e) => { e.stopPropagation(); handleViewProject(filteredProjects[0]); }} className="btn-base btn-purple">
                        View Project
                        <Eye size={16} className="ml-2" />
                      </Button>
                      {filteredProjects[0].github_url && (
                        <Button onClick={(e) => { e.stopPropagation(); window.open(filteredProjects[0].github_url, '_blank'); }} className="btn-base btn-secondary" variant="ghost">
                          <Github size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Projects Two Independent Scrolling Rows */}
          <div className="space-y-8">
            {/* First Row - Always show projects (excluding featured) */}
            <div className="relative">
              <Button
                onClick={scrollLeft}
                className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                variant="ghost"
              >
                <ChevronLeft size={20} className="text-gray-700" />
              </Button>
              
              <Button
                onClick={scrollRight}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                variant="ghost"
              >
                <ChevronRight size={20} className="text-gray-700" />
              </Button>

              <div
                ref={scrollContainer}
                className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {filteredProjects.slice(1, filteredProjects.length <= 5 ? filteredProjects.length : Math.max(5, Math.ceil((filteredProjects.length - 1) / 2) + 1)).map((project) => (
                  <Card
                    key={project.id}
                    className={`group glass-card overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex-shrink-0 w-80 flex flex-col cursor-pointer ${hoveredProject === project.id ? 'ring-2 ring-purple-600/30' : ''}`}
                    onMouseEnter={() => setHoveredProject(project.id)}
                    onMouseLeave={() => setHoveredProject(null)}
                    onClick={() => handleViewProject(project)}
                  >
                    {/* Project Image */}
                    <div className="relative h-48 shrink-0 overflow-hidden rounded-t-2xl">
                      <img
                        src={project.image || '/api/placeholder/400/240'}
                        alt={project.title}
                        className="block w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.src = '/api/placeholder/400/240'; }}
                      />
                      {/* bottom-only fade so the top edge stays crisp */}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/90 via-white/0 to-transparent"></div>

                      {/* Status Badge */}
                      <div className="absolute top-4 right-4">
                        <Badge className={`font-semibold ${project.status === 'Completed' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}`}>
                          {project.status}
                        </Badge>
                      </div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-purple-600 text-white font-medium">
                          {project.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6 flex flex-col flex-grow">
                      {/* Title */}
                      <h3 className="heading-4 font-bold text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300">
                        {project.title}
                      </h3>

                      {/* Description */}
                      <div
                        className="text-text-secondary mb-4 line-clamp-3 leading-relaxed rich-text-content"
                        dangerouslySetInnerHTML={{ __html: project.description }}
                      />

                      {/* Technologies */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {project.technologies?.slice(0, 4)?.map((tech, index) => (
                            <Badge key={index} className="bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium">
                              {tech}
                            </Badge>
                          ))}
                          {project.technologies?.length > 4 && (
                            <Badge className="bg-gray-100 text-gray-600 px-3 py-1 text-sm">
                              +{project.technologies.length - 4}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Impact */}
                      <div className="flex items-start space-x-2 mb-4">
                        <TrendingUp size={16} className="text-emerald-600 mt-1 flex-shrink-0" />
                        <div className="text-sm text-text-secondary">
                          <span className="font-semibold text-emerald-600">Impact:</span>
                          <div className="inline rich-text-content ml-1" dangerouslySetInnerHTML={{ __html: project.impact }} />
                        </div>
                      </div>

                      {/* Updated Date */}
                      <div className="text-xs text-text-muted mb-4">
                        Updated: {new Date(project.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>

                      {/* Action Buttons - Always at bottom */}
                      <div className="flex gap-3 pt-4 border-t border-gray-200 mt-auto">
                        <Button onClick={(e) => { e.stopPropagation(); handleViewProject(project); }} className="btn-base btn-primary btn-sm flex-1">
                          <Eye size={16} className="mr-2" />
                          View Project
                        </Button>
                        <Button onClick={(e) => { e.stopPropagation(); handleViewProject(project); }} className="btn-base btn-secondary btn-sm" variant="ghost">
                          <Code size={16} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Second Row - Only show if more than 4 projects (excluding featured) */}
            {filteredProjects.length > 5 && (
              <div className="relative">
                <Button
                  onClick={scrollLeft2}
                  className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                  variant="ghost"
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </Button>
                
                <Button
                  onClick={scrollRight2}
                  className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                  variant="ghost"
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </Button>

                <div
                  ref={scrollContainer2}
                  className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {filteredProjects.slice(Math.max(5, Math.ceil((filteredProjects.length - 1) / 2) + 1)).map((project) => (
                    <Card
                      key={project.id}
                      className={`group glass-card overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex-shrink-0 w-80 flex flex-col cursor-pointer ${hoveredProject === project.id ? 'ring-2 ring-purple-600/30' : ''}`}
                      onMouseEnter={() => setHoveredProject(project.id)}
                      onMouseLeave={() => setHoveredProject(null)}
                      onClick={() => handleViewProject(project)}
                    >
                      {/* Project Image */}
                      <div className="relative h-48 shrink-0 overflow-hidden rounded-t-2xl">
                        <img
                          src={project.image || '/api/placeholder/400/240'}
                          alt={project.title}
                          className="block w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => { e.target.src = '/api/placeholder/400/240'; }}
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/90 via-white/0 to-transparent"></div>

                        {/* Status Badge */}
                        <div className="absolute top-4 right-4">
                          <Badge className={`font-semibold ${project.status === 'Completed' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}`}>
                            {project.status}
                          </Badge>
                        </div>

                        {/* Category Badge */}
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-purple-600 text-white font-medium">
                            {project.category}
                          </Badge>
                        </div>
                      </div>

                      {/* Project Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        {/* Title */}
                        <h3 className="heading-4 font-bold text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300">
                          {project.title}
                        </h3>

                        {/* Description */}
                        <div
                          className="text-text-secondary mb-4 line-clamp-3 leading-relaxed rich-text-content"
                          dangerouslySetInnerHTML={{ __html: project.description }}
                        />

                        {/* Technologies */}
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {project.technologies?.slice(0, 4)?.map((tech, index) => (
                              <Badge key={index} className="bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium">
                                {tech}
                              </Badge>
                            ))}
                            {project.technologies?.length > 4 && (
                              <Badge className="bg-gray-100 text-gray-600 px-3 py-1 text-sm">
                                +{project.technologies.length - 4}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Impact */}
                        <div className="flex items-start space-x-2 mb-4">
                          <TrendingUp size={16} className="text-emerald-600 mt-1 flex-shrink-0" />
                          <div className="text-sm text-text-secondary">
                            <span className="font-semibold text-emerald-600">Impact:</span>
                            <div className="inline rich-text-content ml-1" dangerouslySetInnerHTML={{ __html: project.impact }} />
                          </div>
                        </div>

                        {/* Action Buttons - Always at bottom */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200 mt-auto">
                          <Button onClick={(e) => { e.stopPropagation(); handleViewProject(project); }} className="btn-base btn-primary btn-sm flex-1">
                            <Eye size={16} className="mr-2" />
                            View Project
                          </Button>
                          <Button onClick={(e) => { e.stopPropagation(); handleViewProject(project); }} className="btn-base btn-secondary btn-sm" variant="ghost">
                            <Code size={16} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* View All Projects Button */}
          <div className="text-center mt-8">
            <Button
              onClick={() => window.open('/projects', '_blank')}
              className="btn-base btn-secondary btn-lg"
            >
              View All Projects
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="heading-3 text-text-primary mb-4">Ready to Innovate Together?</h3>
              <p className="body-medium text-text-secondary mb-6 max-w-2xl mx-auto">
                Let's collaborate on your next automation project or AI implementation initiative. 
                Together, we can drive efficiency and innovation.
              </p>
              <Button className="btn-base btn-primary btn-lg">
                <Zap size={20} className="mr-2" />
                Start a Project
              </Button>
            </div>
          </div>
          {/* End genie animation wrapper */}
        </div>
      </div>

      {/* Project Details Modal - Large Immersive Experience */}
      {showProjectModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white w-full h-full max-w-8xl max-h-[95vh] overflow-hidden rounded-xl shadow-2xl m-4 flex flex-col">
            
            {/* Header Bar - Fixed */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center space-x-4">
                <Badge className={`${getStatusBadgeColor(selectedProject.status)} font-medium`}>
                  {selectedProject.status}
                </Badge>
                <Badge className="bg-blue-100 text-blue-800 font-medium">
                  {selectedProject.category}
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                {selectedProject.github_url && (
                  <Button onClick={() => window.open(selectedProject.github_url, '_blank')} className="btn-base btn-secondary btn-sm">
                    <Github size={16} className="mr-2" />
                    View Code
                  </Button>
                )}
                {selectedProject.demo_url && (
                  <Button onClick={() => window.open(selectedProject.demo_url, '_blank')} className="btn-base btn-primary btn-sm">
                    <ExternalLink size={16} className="mr-2" />
                    Live Demo
                  </Button>
                )}
                <button onClick={handleCloseProjectModal} className="text-text-secondary hover:text-text-primary p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-8 max-w-5xl mx-auto">
                
                {/* Project Title */}
                <h1 className="text-4xl md:text-6xl font-bold text-text-primary mb-8 font-grotesk leading-tight">
                  {selectedProject.title}
                </h1>
                
                {/* Project Image */}
                {(selectedProject.image || '/api/placeholder/800/400') && (
                  <div className="mb-10 overflow-hidden rounded-2xl">
                    <img
                      src={selectedProject.image || '/api/placeholder/800/400'}
                      alt={selectedProject.title}
                      className="block w-full h-96 object-cover object-center"
                      onError={(e) => { e.target.src = '/api/placeholder/800/400'; }}
                    />
                  </div>
                )}
                
                {/* Project Overview */}
                <div className="mb-10">
                  <div
                    className="text-xl text-text-secondary font-medium leading-relaxed rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedProject.description }}
                  />
                </div>
                
                {/* Key Metrics */}
                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl">
                    <div className="flex items-center mb-3">
                      <Target className="text-green-600 mr-3" size={24} />
                      <h3 className="font-semibold text-green-800">Impact</h3>
                    </div>
                    <div className="text-green-700 font-medium rich-text-content" dangerouslySetInnerHTML={{ __html: selectedProject.impact }} />
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl">
                    <div className="flex items-center mb-3">
                      <Calendar className="text-blue-600 mr-3" size={24} />
                      <h3 className="font-semibold text-blue-800">Status</h3>
                    </div>
                    <p className="text-blue-700 font-medium">{selectedProject.status}</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl">
                    <div className="flex items-center mb-3">
                      <Users className="text-purple-600 mr-3" size={24} />
                      <h3 className="font-semibold text-purple-800">Category</h3>
                    </div>
                    <p className="text-purple-700 font-medium">{selectedProject.category}</p>
                  </div>
                </div>
                
                {/* Technologies Used */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-text-primary mb-6">Technologies & Tools</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedProject.technologies.map((tech, index) => (
                      <Badge key={index} className="bg-indigo-100 text-indigo-800 px-4 py-2 text-sm font-medium">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Project Details */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-text-primary mb-6">Project Details</h3>
                  <div className="prose prose-xl max-w-none">
                    {selectedProject.details ? (
                      <div className="space-y-8 text-text-secondary leading-relaxed text-lg rich-text-content" dangerouslySetInnerHTML={{ __html: selectedProject.details }} />
                    ) : (
                      <div className="space-y-8 text-text-secondary leading-relaxed text-lg">
                        <p>
                          This project represents a significant advancement in {selectedProject.category.toLowerCase()}, 
                          demonstrating innovative approaches to solving complex business challenges.
                        </p>
                        <p>
                          Key achievements include {selectedProject.impact.toLowerCase()}, showcasing the practical 
                          application of cutting-edge technologies in real-world scenarios.
                        </p>
                        <p>
                          The implementation leverages {selectedProject.technologies.slice(0, 3).join(', ')}
                          {selectedProject.technologies.length > 3 && ` and ${selectedProject.technologies.length - 3} other technologies`} 
                          to deliver a comprehensive solution that addresses both immediate needs and long-term scalability.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Call to Action */}
                <div className="mt-16 p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl">
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Interested in Similar Solutions?</h3>
                  <p className="text-text-secondary mb-6">
                    Let's discuss how we can implement similar innovations in your organization. 
                    I specialize in {selectedProject.category.toLowerCase()} and would love to explore opportunities for collaboration.
                  </p>
                  <div className="flex gap-4">
                    <Button onClick={() => window.open('https://linkedin.com/in/gideonmetre', '_blank')} className="btn-base btn-primary">
                      Connect on LinkedIn
                    </Button>
                    <Button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="btn-base btn-secondary">
                      Start a Discussion
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TechProjectsSection;
